/** Pure logic for the board's train-selection mechanics, extracted so the
 *  matching rules are unit-tested without React.
 *
 *  A train is built from a run of activities on ONE level; "Create train" then
 *  maps it onto every other level that contains the same set of work codes. */
import { buildTrainFromSelection, findTrainInLeaf } from "./trainReconcile";
import type { SelectionCar } from "./trainReconcile";

/** Confine a multi-leaf selection to ONE leaf: keep only the items on the leaf
 *  of the most-recently-added id (or, if nothing was added, the first item).
 *  A single-item or already-single-leaf selection passes through unchanged.
 *  `leafOf` returns an activity's leaf key, or null if unknown. */
export function confineToOneLeaf(
  next: Set<number>,
  prev: Set<number>,
  leafOf: (id: number) => string | null,
): Set<number> {
  if (next.size <= 1) return next;
  const arr = Array.from(next);
  const added = arr.filter((id) => !prev.has(id));
  const anchorId = added.length ? added[added.length - 1] : arr[0];
  const anchorLeaf = leafOf(anchorId);
  if (anchorLeaf == null) return next;
  const confined = arr.filter((id) => leafOf(id) === anchorLeaf);
  // Already on one leaf — avoid allocating a new Set.
  if (confined.length === arr.length) return next;
  return new Set(confined);
}

/** "Select from here onward": extend a selection to every car at or after the
 *  earliest-positioned selected one (by sequence start). Returns the selection
 *  unchanged when nothing in `cars` is currently selected. */
export function fromHereSelection(
  cars: Array<{ id: number; startIndex: number }>,
  selected: Set<number>,
): Set<number> {
  const sel = cars.filter((c) => selected.has(c.id));
  if (sel.length === 0) return selected;
  const anchor = Math.min(...sel.map((c) => c.startIndex));
  return new Set(cars.filter((c) => c.startIndex >= anchor).map((c) => c.id));
}

/** The activity ids to ghost-highlight as a live preview: exactly the bars
 *  "Create train" would auto-map. Delegates to findTrainInLeaf — the SAME
 *  order-tolerant, code-MULTISET matcher Create uses — so the preview never
 *  lights a level Create then silently drops (e.g. a repeated-code run where a
 *  level only has one copy). Needs a run of 2+ cars. Refs that aren't positive
 *  numeric activity ids (e.g. draft ids) are skipped — only committed bars
 *  preview. */
export function previewRefs(
  selection: SelectionCar[],
  srcLeaf: string | null,
  leafCars: Map<string, SelectionCar[]>,
): Set<number> {
  const out = new Set<number>();
  if (selection.length < 2) return out;
  const train = buildTrainFromSelection(selection, []);
  leafCars.forEach((cars, leafKey) => {
    if (leafKey === srcLeaf) return;
    const inst = findTrainInLeaf(train, cars);
    if (!inst) return; // level lacks the full run — Create would skip it too
    for (const ref of Object.values(inst)) {
      const n = Number(ref);
      if (Number.isFinite(n) && n > 0) out.add(n);
    }
  });
  return out;
}
